import { useCallback, useEffect, useState } from 'react';
import { formatTaskKey, type TaskSummary } from '@todo/shared';
import { taskApi } from '../api/task.api';
import { useTaskActions } from '../hooks/useTaskActions';
import { useColumnBoard } from '../hooks/useColumnBoard';
import { initials } from '../lib/task-ui';
import { TaskDetailPanel } from '../components/TaskDetailPanel';
import {
  TaskCard, TaskSection, TaskListError, TaskListEmpty,
} from '../components/TaskCard';
import { TaskBoardPaged, WORK_COLUMNS } from '../components/TaskBoard';
import { BlockReasonModal } from '../components/BlockReasonModal';
import { useDragToStatus } from '../hooks/useDragToStatus';

/**
 * Module 9 - Assigned by me (Delegation).
 *
 * The delegator's mirror of "Assigned to me": everything I handed out, as a
 * board like My Day - plus a strip above it for cancelled tasks, which is
 * not live work a status column can hold. Every card names its assignees,
 * because the question this page answers is "where is the work I gave away?"
 *
 * The board's 4 status columns load and page independently (see
 * useColumnBoard); cancelled tasks are fetched separately (capped at 100,
 * unpaginated) since they are not live work someone needs to keep scrolling
 * through.
 */

function isOpen(task: TaskSummary): boolean {
  return task.status === 'PENDING' || task.status === 'ONGOING' || task.status === 'BLOCKED';
}

const FILTERS = { scope: 'created-by-me' as const };

export default function AssignedByMePage() {
  const [flash, setFlash] = useState<string | null>(null);
  /** Two-step cancel: first click arms, second confirms. */
  const [confirmCancelId, setConfirmCancelId] = useState<string | null>(null);
  const [viewing, setViewing] = useState<TaskSummary | null>(null);

  const board = useColumnBoard(WORK_COLUMNS, FILTERS);

  // Cancelled tasks are not live work - My Day's board leaves them out of
  // its columns for the same reason, so here they get a strip of their own
  // above the board instead of a column, fetched separately (not
  // paginated: 100 is already a generous cap for tasks nobody needs to
  // actively page through).
  const [cancelled, setCancelledTasks] = useState<TaskSummary[]>([]);
  const [cancelledLoading, setCancelledLoading] = useState(true);

  const loadCancelled = useCallback(async () => {
    setCancelledLoading(true);
    try {
      const res = await taskApi.list({ scope: 'created-by-me', status: 'CANCELLED', pageSize: 100 });
      setCancelledTasks(res.data);
    } catch {
      // The board's own error banner already covers a failed load; this
      // strip just quietly stays empty rather than doubling up on errors.
    } finally {
      setCancelledLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadCancelled();
  }, [loadCancelled]);

  function replace(updated: TaskSummary) {
    board.mutate(updated);
    setCancelledTasks((current) => {
      const withoutIt = current.filter((t) => t.id !== updated.id);
      return updated.status === 'CANCELLED' ? [updated, ...withoutIt] : withoutIt;
    });
  }

  const actions = useTaskActions(replace, setFlash);
  const drag = useDragToStatus(
    (id) => board.allLoaded.find((t) => t.id === id),
    replace,
    setFlash,
  );

  async function cancel(task: TaskSummary) {
    if (await actions.setStatus(task, 'CANCELLED')) {
      setFlash(`${formatTaskKey(task.taskKey)} cancelled.`);
    }
    setConfirmCancelId(null);
  }

  const loading = board.loading || cancelledLoading;
  const totalAll = board.totalCount + cancelled.length;

  function cardFor(task: TaskSummary, draggable = false, subtasks?: TaskSummary[]) {
    const busy = actions.busyId === task.id || drag.busyId === task.id;
    const open = isOpen(task);

    return (
      <TaskCard
        key={task.id}
        task={task}
        subtasks={subtasks}
        busy={busy}
        onOpen={setViewing}
        onToggleImportant={(t) => void actions.toggleImportant(t)}
        hideAssignee
        draggable={draggable}
        footer={
          <>
            {/* Who has this task - names only, now that responses are gone. */}
            {task.assignees.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {task.assignees.map((a) => (
                  <span
                    key={a.userId}
                    className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600 ring-1 ring-slate-200"
                  >
                    <span className="grid h-4 w-4 place-items-center rounded-full bg-white/70 text-[8px] font-bold">
                      {initials(a.displayName)}
                    </span>
                    {a.displayName.split(/\s+/)[0]}
                  </span>
                ))}
              </div>
            )}

            {open && (
              <div className="mt-2">
                {confirmCancelId === task.id ? (
                  <span className="flex items-center gap-2">
                    <span className="text-xs text-slate-500">Cancel this task?</span>
                    <button type="button" disabled={busy}
                      onClick={() => void cancel(task)}
                      className="rounded-lg bg-red-600 px-2.5 py-1 text-xs font-medium text-white transition hover:bg-red-700 disabled:opacity-50">
                      Yes, cancel
                    </button>
                    <button type="button"
                      onClick={() => setConfirmCancelId(null)}
                      className="rounded-lg border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-600 transition hover:bg-slate-50">
                      Keep it
                    </button>
                  </span>
                ) : (
                  <button type="button" disabled={busy}
                    onClick={() => setConfirmCancelId(task.id)}
                    className="text-xs font-medium text-slate-400 transition hover:text-red-600">
                    Cancel task&hellip;
                  </button>
                )}
              </div>
            )}
          </>
        }
      />
    );
  }

  const error = board.error;

  return (
    <div className="mx-auto px-8 py-8">
      <header>
        <h1 className="text-xl font-semibold text-slate-900">Assigned by me</h1>
        <p className="mt-1 text-sm text-slate-500">
          {loading
            ? 'Loading...'
            : `${totalAll} ${totalAll === 1 ? 'task' : 'tasks'} you handed out`}
        </p>
      </header>

      {flash && (
        <div role="status" className="mt-5 flex items-start justify-between gap-3 rounded-lg bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-800 ring-1 ring-emerald-200">
          <span>{flash}</span>
          <button type="button" onClick={() => setFlash(null)} aria-label="Dismiss"
                  className="text-emerald-600 hover:text-emerald-800">&times;</button>
        </div>
      )}

      <div className="mt-6 space-y-7">
        {error ? (
          <TaskListError message={error} onRetry={() => { void board.reload(); void loadCancelled(); }} />
        ) : !loading && totalAll === 0 ? (
          <TaskListEmpty
            title="You have not assigned any tasks"
            hint="Create a task in Tasks and choose who does it - it will show up here."
          />
        ) : (
          <>
            {!cancelledLoading && cancelled.length > 0 && (
              <TaskSection
                label="Cancelled"
                dot="bg-slate-300"
                count={cancelled.length}
                hint="Cancelled tasks stay here for the record."
              >
                {cancelled.map((task) => cardFor(task))}
              </TaskSection>
            )}

            <TaskBoardPaged
              state={board.state}
              loading={board.loading}
              onLoadMore={(column) => void board.loadMore(column)}
              renderCard={(task, subtasks) => cardFor(task, true, subtasks)}
              onDropTask={drag.onDropTask}
            />
          </>
        )}
      </div>

      {drag.blocking && (
        <BlockReasonModal
          task={drag.blocking}
          reason={drag.blockReason}
          busy={drag.busyId === drag.blocking.id}
          onReasonChange={drag.setBlockReason}
          onConfirm={drag.confirmBlock}
          onCancel={drag.cancelBlock}
        />
      )}

      {viewing && (
        <TaskDetailPanel
          task={viewing}
          onClose={() => setViewing(null)}
          onChanged={(t) => {
            replace(t);
            setViewing((v) => (v && v.id === t.id ? t : v));
          }}
        />
      )}
    </div>
  );
}
